import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { CollisionDetection, DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Add, Close, DeleteOutline, DragIndicator } from '@mui/icons-material'
import { Box, Button, Chip, Dialog, IconButton, TextField, Typography } from '@mui/material'
import type { ChapterSectionDependency } from '@shared/chapterSectionDependencies'
import { chapterSectionKey } from '@shared/chapterSectionsOrder'
import { ELEMENTS_ROOT_TAG_ID } from '@shared/tags/canonicalElementsTags'
import { type TagDefinition, createTagId } from '@shared/tags/tagRegistry'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import SearchPreviewPanel from '../../../../components/search/SearchPreviewPanel'
import { SectionTagRail } from '../../../../components/tags/SectionTagRail'
import { type Book, type ContentFile, apiService } from '../../../../services/api'
import { logger } from '../../../../utils/logger'
import { sectionTagsAnnotationId } from '../../../../utils/sectionTagsAnnotationId'
import type { ChapterSectionEntry } from '../../hooks/useChapterSectionsBook'
import type { SectionDependencyView } from '../../hooks/useSectionDependencies'
import { useSectionPreview } from '../../hooks/useSectionPreview'
import SectionDependencyChip from '../dependencies/SectionDependencyChip'
import DroppableContainer from '../organize-chapters/DroppableContainer'
import OrganizeSectionRow from '../organize-chapters/OrganizeSectionRow'

const UNASSIGNED_ID = '__unassigned__'
const ZONE_PREFIX = 'zone:'

type TagMap = Record<string, TagDefinition>

interface ElementCol {
  id: string
  name: string
  sections: string[]
}

interface Draft {
  elements: ElementCol[]
  unassigned: string[]
}

function elementChildIds(tags: TagMap): Set<string> {
  const ids = new Set<string>()
  for (const [id, def] of Object.entries(tags)) {
    if ((def.parentIds || []).includes(ELEMENTS_ROOT_TAG_ID)) {
      ids.add(id)
    }
  }
  return ids
}

function cloneDraft(d: Draft): Draft {
  return { elements: d.elements.map((e) => ({ ...e, sections: [...e.sections] })), unassigned: [...d.unassigned] }
}

/** A full-width element lane that can be dragged (by its handle) to reorder among lanes. */
function SortableLane({ id, children }: { id: string; children: (handle: Record<string, unknown>) => ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
    >
      {children({ ...attributes, ...listeners })}
    </Box>
  )
}

/** A bounded-width, draggable section card (the organize-dialog widget) for the wrapped lane layout. */
function SortableSectionCard({
  sectionKey,
  displayText,
  ancestorPath,
  bookFontFamily,
  bookDirection,
  tagRail,
  dependencyChips,
  onOpen,
  isSelected,
  isDraggedAway,
  onSelect,
}: {
  sectionKey: string
  displayText: string
  ancestorPath: string
  bookFontFamily?: string
  bookDirection?: 'ltr' | 'rtl'
  tagRail?: ReactNode
  dependencyChips?: ReactNode
  onOpen?: () => void
  isSelected?: boolean
  isDraggedAway?: boolean
  onSelect?: (key: string, shiftKey: boolean, metaKey: boolean) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sectionKey })
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      sx={{
        width: 244,
        maxWidth: '100%',
        opacity: isDragging || isDraggedAway ? 0.3 : 1,
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        borderRadius: 1,
        bgcolor: isSelected ? 'action.selected' : 'background.paper',
        touchAction: 'manipulation',
        '&:hover': { borderColor: isSelected ? 'primary.main' : 'text.secondary' },
      }}
    >
      <Box onClick={(e) => onSelect?.(sectionKey, e.shiftKey, e.metaKey || e.ctrlKey)}>
        <OrganizeSectionRow
          displayText={displayText}
          ancestorPath={ancestorPath}
          bookFontFamily={bookFontFamily}
          bookDirection={bookDirection}
          tagRail={tagRail}
          dependencyChips={dependencyChips}
          onPreview={onOpen}
        />
      </Box>
    </Box>
  )
}

interface ElementsBoardDialogProps {
  open: boolean
  onClose: () => void
  entries: ChapterSectionEntry[]
  entryByKey: Map<string, ChapterSectionEntry>
  bookId?: string
  bookFontFamily?: string
  bookDirection?: 'ltr' | 'rtl'
  tagRegistry?: TagMap
  tagColorMap?: Record<string, string>
  elementSectionOrder?: Record<string, string[]>
  onSectionTagsPersisted?: () => void
  onRegistrySaved?: (tags: TagMap) => void
  onElementSectionOrderChange?: (order: Record<string, string[]>) => Promise<void>
  book?: Book | null
  onSectionContentSaved?: (filename: string, updatedFile: ContentFile) => void
  /** Shared dependency view; the chip renders only for sections that have deps. */
  dependencies?: SectionDependencyView
  onEditDependency?: (dep: ChapterSectionDependency) => void
  onRemoveDependency?: (depId: string) => void
}

export default function ElementsBoardDialog({
  open,
  onClose,
  entries,
  entryByKey,
  bookId,
  bookFontFamily,
  bookDirection = 'ltr',
  tagRegistry,
  tagColorMap,
  elementSectionOrder,
  onSectionTagsPersisted,
  onRegistrySaved,
  onElementSectionOrderChange,
  book,
  onSectionContentSaved,
  dependencies,
  onEditDependency,
  onRemoveDependency,
}: ElementsBoardDialogProps) {
  const persistedFamilyIds = useMemo(() => elementChildIds(tagRegistry ?? {}), [tagRegistry])

  const [draft, setDraft] = useState<Draft>(() => {
    const tags = tagRegistry ?? {}
    const children = Object.entries(tags)
      .filter(([, def]) => (def.parentIds || []).includes(ELEMENTS_ROOT_TAG_ID))
      .map(([id, def]) => ({ id, name: def.name, order: typeof def.order === 'number' ? def.order : 0 }))
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, 'he'))
    const family = new Set(children.map((c) => c.id))
    const buckets = new Map<string, string[]>(children.map((c) => [c.id, []]))
    const unassigned: string[] = []
    for (const entry of entries) {
      const key = chapterSectionKey(entry.chapter.filename, entry.headingBlockId)
      const eid = entry.tagIds.find((t) => family.has(t))
      if (eid && buckets.has(eid)) {
        buckets.get(eid)!.push(key)
      } else {
        unassigned.push(key)
      }
    }
    const elements = children.map((c) => {
      const present = buckets.get(c.id) ?? []
      const ord = elementSectionOrder?.[c.id] ?? []
      const sections = [...ord.filter((k) => present.includes(k)), ...present.filter((k) => !ord.includes(k))]
      return { id: c.id, name: c.name, sections }
    })
    return { elements, unassigned }
  })

  const [registryDirty, setRegistryDirty] = useState(false)
  const [contentDirty, setContentDirty] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)

  const {
    previewKey,
    previewAst,
    previewLoading,
    previewError,
    previewTitle,
    previewDirty,
    previewSaving,
    previewRecentlySaved,
    openPreview,
    closePreview,
    onPreviewChange,
    onPreviewSave,
  } = useSectionPreview({ open, bookId, entryByKey, onSectionContentSaved })

  const dirty = registryDirty || contentDirty || editingId !== null

  const elementIdSet = useMemo(() => new Set(draft.elements.map((e) => e.id)), [draft.elements])

  // Multi-select (mirrors useOrganizeChaptersState's selection model so several cards drag together).
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const lastClickedKeyRef = useRef<string | null>(null)
  const draggedKeysRef = useRef<string[]>([])

  const allKeysInOrder = useMemo(() => {
    const keys: string[] = []
    for (const e of draft.elements) {
      keys.push(...e.sections)
    }
    keys.push(...draft.unassigned)
    return keys
  }, [draft])

  const toggleSelection = useCallback(
    (key: string, shiftKey: boolean, metaKey: boolean) => {
      setSelectedKeys((prev) => {
        const next = new Set(prev)
        if (shiftKey && lastClickedKeyRef.current) {
          const from = allKeysInOrder.indexOf(lastClickedKeyRef.current)
          const to = allKeysInOrder.indexOf(key)
          if (from !== -1 && to !== -1) {
            for (let i = Math.min(from, to); i <= Math.max(from, to); i++) {
              next.add(allKeysInOrder[i])
            }
          }
        } else if (metaKey) {
          if (next.has(key)) {
            next.delete(key)
          } else {
            next.add(key)
          }
        } else if (next.size === 1 && next.has(key)) {
          next.clear()
        } else {
          next.clear()
          next.add(key)
        }
        lastClickedKeyRef.current = key
        return next
      })
    },
    [allKeysInOrder],
  )

  const clearSelection = useCallback(() => setSelectedKeys(new Set()), [])

  const getDraggedKeys = useCallback(
    (activeKey: string): string[] =>
      selectedKeys.has(activeKey) && selectedKeys.size > 1
        ? allKeysInOrder.filter((k) => selectedKeys.has(k))
        : [activeKey],
    [selectedKeys, allKeysInOrder],
  )

  // Esc clears the selection first (capture phase, before MUI's close handler); an Esc with
  // nothing selected falls through and closes the dialog as usual.
  useEffect(() => {
    if (!open) {
      return
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || previewKey || editingId) {
        return
      }
      if (selectedKeys.size > 0) {
        e.stopPropagation()
        clearSelection()
      }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [open, previewKey, editingId, selectedKeys, clearSelection])

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 5 } }),
  )

  const findContainer = useCallback(
    (key: string): string | null => {
      for (const e of draft.elements) {
        if (e.sections.includes(key)) {
          return e.id
        }
      }
      return draft.unassigned.includes(key) ? UNASSIGNED_ID : null
    },
    [draft],
  )

  const resolveContainer = useCallback(
    (overId: string): string | null => {
      if (overId.startsWith(ZONE_PREFIX)) {
        return overId.slice(ZONE_PREFIX.length)
      }
      return findContainer(overId)
    },
    [findContainer],
  )

  const detectCollisions = useCallback<CollisionDetection>(
    (args) => {
      const id = String(args.active.id)
      const isElementDrag = elementIdSet.has(id)
      const filtered = args.droppableContainers.filter((c) =>
        isElementDrag ? elementIdSet.has(String(c.id)) : !elementIdSet.has(String(c.id)),
      )
      // pointerWithin makes the whole lane a drop target (closestCenter only "wins" near the
      // center of a wide empty lane); fall back to closestCenter when the pointer is over nothing.
      const pointer = pointerWithin({ ...args, droppableContainers: filtered })
      if (pointer.length > 0) {
        const ids = new Set(pointer.map((p) => p.id))
        return closestCenter({ ...args, droppableContainers: filtered.filter((c) => ids.has(c.id)) })
      }
      return closestCenter({ ...args, droppableContainers: filtered })
    },
    [elementIdSet],
  )

  const onDragStart = useCallback(
    (e: DragStartEvent) => {
      const activeKey = String(e.active.id)
      setActiveId(activeKey)
      if (elementIdSet.has(activeKey)) {
        draggedKeysRef.current = []
        return
      }
      if (!selectedKeys.has(activeKey)) {
        clearSelection()
        draggedKeysRef.current = [activeKey]
      } else {
        draggedKeysRef.current = getDraggedKeys(activeKey)
      }
    },
    [elementIdSet, selectedKeys, clearSelection, getDraggedKeys],
  )

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e
      setActiveId(null)
      const draggedKeys = draggedKeysRef.current.length ? [...draggedKeysRef.current] : [String(active.id)]
      draggedKeysRef.current = []
      if (!over) {
        return
      }
      const activeKey = String(active.id)
      const overId = String(over.id)

      // Reorder lanes.
      if (elementIdSet.has(activeKey)) {
        if (elementIdSet.has(overId) && activeKey !== overId) {
          setDraft((prev) => {
            const oldIdx = prev.elements.findIndex((el) => el.id === activeKey)
            const newIdx = prev.elements.findIndex((el) => el.id === overId)
            if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) {
              return prev
            }
            return { ...prev, elements: arrayMove(prev.elements, oldIdx, newIdx) }
          })
          setRegistryDirty(true)
        }
        return
      }

      const from = findContainer(activeKey)
      const to = resolveContainer(overId)
      if (!from || !to) {
        return
      }
      const draggedSet = new Set(draggedKeys)

      // Reorder within the same lane (committed on drop — no live reflow while aiming).
      if (from === to) {
        if (activeKey === overId || overId.startsWith(ZONE_PREFIX)) {
          return
        }
        setDraft((prev) => {
          const next = cloneDraft(prev)
          const list = to === UNASSIGNED_ID ? next.unassigned : next.elements.find((el) => el.id === to)!.sections
          const oldIdx = list.indexOf(activeKey)
          const newIdx = list.indexOf(overId)
          if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) {
            return prev
          }
          const filtered = list.filter((k) => !draggedSet.has(k))
          const overIdxInFiltered = filtered.indexOf(overId)
          if (overIdxInFiltered === -1) {
            filtered.push(...draggedKeys)
          } else {
            const insertIdx = oldIdx < newIdx ? overIdxInFiltered + 1 : overIdxInFiltered
            filtered.splice(insertIdx, 0, ...draggedKeys)
          }
          if (to === UNASSIGNED_ID) {
            next.unassigned = filtered
          } else {
            next.elements.find((el) => el.id === to)!.sections = filtered
          }
          return next
        })
        setContentDirty(true)
        return
      }

      // Move to a different lane.
      setDraft((prev) => {
        const next = cloneDraft(prev)
        for (const el of next.elements) {
          el.sections = el.sections.filter((k) => !draggedSet.has(k))
        }
        next.unassigned = next.unassigned.filter((k) => !draggedSet.has(k))
        const list = to === UNASSIGNED_ID ? next.unassigned : next.elements.find((el) => el.id === to)?.sections
        if (!list) {
          return prev
        }
        const idx = overId.startsWith(ZONE_PREFIX) ? -1 : list.indexOf(overId)
        if (idx === -1) {
          list.push(...draggedKeys)
        } else {
          list.splice(idx, 0, ...draggedKeys)
        }
        return next
      })
      setContentDirty(true)
    },
    [elementIdSet, findContainer, resolveContainer],
  )

  // --- element CRUD (draft only) ---

  const startEdit = useCallback((id: string, name: string) => {
    setEditingId(id)
    setEditValue(name)
  }, [])

  const commitEdit = useCallback(() => {
    const id = editingId
    setEditingId(null)
    if (!id) {
      return
    }
    const name = editValue.trim()
    setDraft((prev) => {
      const el = prev.elements.find((e) => e.id === id)
      if (!el || !name || el.name === name) {
        return prev
      }
      return { ...prev, elements: prev.elements.map((e) => (e.id === id ? { ...e, name } : e)) }
    })
    setRegistryDirty(true)
  }, [editingId, editValue])

  const addElement = useCallback(() => {
    const existing = new Set(draft.elements.map((e) => e.id))
    let id = createTagId('יסוד חדש')
    while (existing.has(id)) {
      id = createTagId('יסוד חדש')
    }
    setDraft((prev) => ({ ...prev, elements: [...prev.elements, { id, name: 'יסוד חדש', sections: [] }] }))
    setRegistryDirty(true)
    setEditingId(id)
    setEditValue('יסוד חדש')
  }, [draft.elements])

  const deleteElement = useCallback((id: string) => {
    setDraft((prev) => {
      const el = prev.elements.find((e) => e.id === id)
      const freed = el?.sections ?? []
      return { elements: prev.elements.filter((e) => e.id !== id), unassigned: [...prev.unassigned, ...freed] }
    })
    setRegistryDirty(true)
    setContentDirty(true)
  }, [])

  const clearAll = useCallback(() => {
    if (draft.elements.length === 0) {
      return
    }
    if (!window.confirm(`למחוק את כל ${draft.elements.length} היסודות? (ייכנס לתוקף בשמירה)`)) {
      return
    }
    setDraft((prev) => ({
      elements: [],
      unassigned: [...prev.unassigned, ...prev.elements.flatMap((e) => e.sections)],
    }))
    setRegistryDirty(true)
    setContentDirty(true)
  }, [draft.elements.length])

  const handleClose = useCallback(() => {
    if (dirty && !window.confirm('לצאת בלי לשמור? השינויים שעשית יימחקו.')) {
      return
    }
    onClose()
  }, [dirty, onClose])

  const handleSave = useCallback(async () => {
    if (!bookId || saving) {
      return
    }
    setSaving(true)
    try {
      let elements = draft.elements
      if (editingId) {
        const name = editValue.trim()
        if (name) {
          elements = elements.map((e) => (e.id === editingId ? { ...e, name } : e))
        }
        setEditingId(null)
      }

      if (registryDirty || editingId) {
        const nextTags: TagMap = { ...(tagRegistry ?? {}) }
        for (const id of persistedFamilyIds) {
          delete nextTags[id]
        }
        elements.forEach((e, i) => {
          nextTags[e.id] = { name: e.name, parentIds: [ELEMENTS_ROOT_TAG_ID], order: i + 1 }
        })
        const res = await apiService.updateTagRegistry(bookId, { version: 2, tags: nextTags })
        onRegistrySaved?.((res.data?.tags ?? nextTags) as TagMap)
      }

      if (contentDirty || registryDirty) {
        const finalIds = new Set(elements.map((e) => e.id))
        const stripIds = new Set([...persistedFamilyIds, ...finalIds])
        const keyToElement = new Map<string, string>()
        for (const e of elements) {
          for (const k of e.sections) {
            keyToElement.set(k, e.id)
          }
        }
        let assignChanged = false
        for (const entry of entries) {
          const key = chapterSectionKey(entry.chapter.filename, entry.headingBlockId)
          const target = keyToElement.get(key) ?? null
          const persisted = entry.tagIds.find((t) => stripIds.has(t)) ?? null
          if (target === persisted) {
            continue
          }
          const nextTags = [...entry.tagIds.filter((t) => !stripIds.has(t)), ...(target ? [target] : [])]
          await apiService.updateAnnotation(bookId, entry.chapter.filename, {
            id: sectionTagsAnnotationId(entry.headingBlockId),
            tags: nextTags,
            anchor: { type: 'block', blockId: entry.headingBlockId },
            createdAt: new Date(),
          })
          assignChanged = true
        }
        if (assignChanged) {
          onSectionTagsPersisted?.()
        }
        if (onElementSectionOrderChange) {
          await onElementSectionOrderChange(Object.fromEntries(elements.map((e) => [e.id, e.sections])))
        }
      }
      onClose()
    } catch (err) {
      logger.error('Failed to save elements', err)
      toast.error('השמירה נכשלה')
    } finally {
      setSaving(false)
    }
  }, [
    bookId,
    saving,
    draft.elements,
    editingId,
    editValue,
    registryDirty,
    contentDirty,
    tagRegistry,
    persistedFamilyIds,
    entries,
    onRegistrySaved,
    onSectionTagsPersisted,
    onElementSectionOrderChange,
    onClose,
  ])

  const ancestorPathOf = useCallback(
    (entry: ChapterSectionEntry) =>
      [entry.chapter.title, ...entry.ancestorLabels]
        .map((s) => s.trim())
        .filter(Boolean)
        .join(' · '),
    [],
  )

  const draggedSet = new Set<string>(activeId ? draggedKeysRef.current : [])

  const renderSectionList = (sectionKeys: string[]) =>
    sectionKeys.map((key) => {
      const entry = entryByKey.get(key)
      if (!entry) {
        return null
      }
      const tagRail = tagRegistry ? (
        <SectionTagRail
          tagIds={entry.tagIds.filter((t) => !persistedFamilyIds.has(t))}
          tagRegistry={tagRegistry}
          tagColorMap={tagColorMap}
          onRequestEdit={() => openPreview(key)}
        />
      ) : undefined
      return (
        <SortableSectionCard
          key={key}
          sectionKey={key}
          displayText={entry.displayText}
          ancestorPath={ancestorPathOf(entry)}
          bookFontFamily={bookFontFamily}
          bookDirection={bookDirection}
          tagRail={tagRail}
          dependencyChips={
            dependencies ? (
              <SectionDependencyChip
                sectionKey={key}
                view={dependencies}
                bookDirection={bookDirection ?? 'ltr'}
                bookFontFamily={bookFontFamily}
                onRemove={(depId) => onRemoveDependency?.(depId)}
                onEdit={(dep) => onEditDependency?.(dep)}
              />
            ) : undefined
          }
          onOpen={() => openPreview(key)}
          isSelected={selectedKeys.has(key)}
          isDraggedAway={activeId !== null && key !== activeId && draggedSet.has(key)}
          onSelect={toggleSelection}
        />
      )
    })

  const renderLaneBody = (label: ReactNode, sectionKeys: string[], zoneId: string) => (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'stretch', mb: 0.75 }}>
      <Box sx={{ flex: '0 0 168px', py: 0.5 }}>{label}</Box>
      <Box sx={{ flex: 1, borderRadius: 1, border: '1px dashed', borderColor: 'divider', p: 0.5 }}>
        <DroppableContainer id={`${ZONE_PREFIX}${zoneId}`}>
          <SortableContext items={sectionKeys} strategy={rectSortingStrategy}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignContent: 'flex-start' }}>
              {renderSectionList(sectionKeys)}
            </Box>
          </SortableContext>
        </DroppableContainer>
      </Box>
    </Box>
  )

  const activeEntry = activeId && !elementIdSet.has(activeId) ? entryByKey.get(activeId) : null
  const activeElement = activeId && elementIdSet.has(activeId) ? draft.elements.find((e) => e.id === activeId) : null
  const dragCount = draggedKeysRef.current.length
  const unassignedCount = draft.unassigned.length
  const placedCount = entries.length - unassignedCount

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullScreen PaperProps={{ sx: { bgcolor: 'background.default' } }}>
        <Box dir={bookDirection} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <IconButton size="small" onClick={handleClose} aria-label="סגירה">
              <Close fontSize="small" />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                יסודות — מיון מקטעים
                {dirty ? ' •' : ''}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                גרור מקטעים בין יסודות · בחר כמה (Shift/⌘) וגרור יחד · גרור יסוד מהידית · לחץ שם לעריכה · עין לתצוגה ·
                נשמר בשמירה · משובצים {placedCount} · לא משובץ {unassignedCount}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <DndContext
              sensors={sensors}
              collisionDetection={detectCollisions}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            >
              {renderLaneBody(
                <>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>לא משובץ</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{unassignedCount} מקטעים</Typography>
                </>,
                draft.unassigned,
                UNASSIGNED_ID,
              )}

              <Box sx={{ height: 10 }} />

              <SortableContext items={draft.elements.map((e) => e.id)} strategy={verticalListSortingStrategy}>
                {draft.elements.map((el) => (
                  <SortableLane key={el.id} id={el.id}>
                    {(handleProps) =>
                      renderLaneBody(
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                            <Box
                              {...handleProps}
                              sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', color: 'text.secondary' }}
                              aria-label="גרירת יסוד"
                            >
                              <DragIndicator sx={{ fontSize: 18 }} />
                            </Box>
                            {editingId === el.id ? (
                              <TextField
                                variant="standard"
                                size="small"
                                autoFocus
                                value={editValue}
                                onChange={(ev) => setEditValue(ev.target.value)}
                                onBlur={commitEdit}
                                onKeyDown={(ev) => {
                                  if (ev.key === 'Enter') {
                                    ev.preventDefault()
                                    ev.stopPropagation()
                                    commitEdit()
                                  } else if (ev.key === 'Escape') {
                                    ev.stopPropagation()
                                    setEditingId(null)
                                  }
                                }}
                                inputProps={{ style: { fontSize: 13.5, fontWeight: 500 } }}
                              />
                            ) : (
                              <Typography
                                onClick={() => startEdit(el.id, el.name)}
                                sx={{
                                  fontSize: 13.5,
                                  fontWeight: 500,
                                  cursor: 'text',
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  borderRadius: 0.5,
                                  px: 0.25,
                                  '&:hover': { bgcolor: 'action.hover' },
                                }}
                              >
                                {el.name}
                              </Typography>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => deleteElement(el.id)}
                              aria-label="מחיקת יסוד"
                              sx={{ p: 0.25, flexShrink: 0, '&:hover': { color: 'error.main' } }}
                            >
                              <DeleteOutline sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Box>
                          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                            {el.sections.length} מקטעים
                          </Typography>
                        </>,
                        el.sections,
                        el.id,
                      )
                    }
                  </SortableLane>
                ))}
              </SortableContext>

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button startIcon={<Add />} size="small" onClick={addElement}>
                  הוסף יסוד
                </Button>
                {draft.elements.length > 0 && (
                  <Button size="small" color="error" onClick={clearAll}>
                    מחק את כל היסודות
                  </Button>
                )}
              </Box>

              <DragOverlay>
                {activeElement ? (
                  <Box
                    sx={{
                      opacity: 0.9,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      bgcolor: 'background.paper',
                      minWidth: 160,
                    }}
                  >
                    <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{activeElement.name}</Typography>
                  </Box>
                ) : activeEntry ? (
                  <Box
                    sx={{
                      position: 'relative',
                      opacity: 0.9,
                      pointerEvents: 'none',
                      width: 244,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <OrganizeSectionRow
                      displayText={activeEntry.displayText}
                      ancestorPath={ancestorPathOf(activeEntry)}
                      isSelected
                      bookFontFamily={bookFontFamily}
                      bookDirection={bookDirection}
                    />
                    {dragCount > 1 && (
                      <Chip
                        label={dragCount}
                        size="small"
                        color="primary"
                        sx={{ position: 'absolute', top: -10, insetInlineEnd: -10, height: 22, fontWeight: 700 }}
                      />
                    )}
                  </Box>
                ) : null}
              </DragOverlay>
            </DndContext>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              px: 2,
              py: 1.25,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button color="inherit" onClick={handleClose}>
              ביטול
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}>
              שמירה
            </Button>
          </Box>
        </Box>
      </Dialog>
      {previewKey && (
        <Dialog
          open
          onClose={closePreview}
          maxWidth={false}
          PaperProps={{ sx: { width: 'min(72vw, 920px)', height: 'min(78vh, 760px)', direction: bookDirection } }}
        >
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
            <SearchPreviewPanel
              previewIdentity={previewKey}
              previewTitle={previewTitle}
              previewAst={previewAst}
              previewLoading={previewLoading}
              previewError={previewError}
              book={book ?? null}
              editable={!previewError && !previewLoading && Boolean(previewAst)}
              onChange={onPreviewChange}
              onSave={onPreviewSave}
              saving={previewSaving}
              hasUnsavedChanges={previewDirty}
              recentlySaved={previewRecentlySaved}
            />
          </Box>
        </Dialog>
      )}
    </>
  )
}
